import React, { useMemo } from "react";
import { PageHeader, Card, StatusPill, Money } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { money, num, str, fmtDate, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";

const GrantTrackerPage = () => {
  const grants = useAirtable("grants");
  const clients = useAirtable("clients");
  const clientMap = useMemo(() => indexById(clients.records), [clients.records]);
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  if (grants.loading || clients.loading) return <Loading label="Loading grants" />;
  if (grants.error) return <ErrorState message={grants.error} onRetry={grants.refetch} />;

  const rows = grants.records
    .map((r) => {
      const f = r.fields || {};
      return {
        id: r.id,
        client: linkedName(f["Client Link"], clientMap),
        grant: str(f["Grant Name"]),
        grantor: str(f["Grantor"]),
        applied: money(f["Amount Applied For"]),
        dateApplied: f["Date Applied"],
        deadline: f["Application Deadline"],
        daysUntil: num(f["Days Until Deadline"]),
        status: f["Status"],
      };
    })
    .sort((a, b) => (a.daysUntil ?? 99999) - (b.daysUntil ?? 99999));

  const visibleRows = rows.filter((r) => filterByDate(r.deadline));

  return (
    <div data-testid="grant-tracker-page">
      <PageHeader
        eyebrow="DASHBOARD / GRANT READINESS & RESEARCH"
        title="Grant Readiness & Research"
        subtitle={`${visibleRows.length} of ${rows.length} applications • ${scope === "period" ? "filtered by Application Deadline" : "all-time view"} • Ordered by deadline ascending.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="grant-scope" />
            <ExportButtons
              title="Grant Readiness & Research"
              filename="grant-readiness-and-funding-research"
              testidPrefix="export-grants"
              columns={[
                { key: "client", label: "Client" },
                { key: "grant", label: "Grant" },
                { key: "grantor", label: "Grantor" },
                { key: "applied", label: "Amount Applied" },
                { key: "deadline", label: "Deadline" },
                { key: "daysUntil", label: "Days Until" },
                { key: "status", label: "Status" },
              ]}
              rows={visibleRows}
            />
          </div>
        }
      />

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="text-left">
              {["Client","Grant","Grantor","Amount Applied","Deadline","Days Until","Status"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-[#5E4D40] text-sm">No grant applications for this filter.</td></tr>}
            {visibleRows.map((g, i) => {
              const days = g.daysUntil;
              const isUrgent = days !== null && days >= 0 && days < 14;
              const isPast = days !== null && days < 0;
              return (
                <tr key={g.id} data-testid={`grant-row-${i}`} className="bj-table-row">
                  <td className="py-3 pr-3 text-[#2A1C15] text-sm">{g.client}</td>
                  <td className="py-3 pr-3 text-[#6E5022] text-sm">{g.grant}</td>
                  <td className="py-3 pr-3 text-[#5E4D40] text-sm">{g.grantor}</td>
                  <td className="py-3 pr-3 text-sm"><Money value={g.applied} tone="white" /></td>
                  <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(g.deadline)}</td>
                  <td className={`py-3 pr-3 font-heading text-lg bj-tnum ${isPast ? "text-[#5E4D40]" : isUrgent ? "text-[#5C1A24]" : "text-[#2A1C15]"}`}>
                    {days === null ? "—" : isPast ? `${Math.abs(days)}d ago` : `${days}d`}
                  </td>
                  <td className="py-3 pr-3">{g.status ? <StatusPill status={g.status} /> : <span className="text-[#5E4D40] text-sm">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default GrantTrackerPage;
